// Shared tracker-table column configuration used by the table, the column picker, and the context.
//
// Pinned columns are always visible and can't be moved or removed (Shipment, Status, Alert — in
// that order, as the first three columns). Optional columns can be toggled and reordered by the
// user; their visibility + order is persisted to the profile as `trackerMapColumns` (an ordered
// list of the visible optional ids).

export const PINNED_COLUMN_IDS = ['shipment', 'status', 'alert'] as const

export const DEFAULT_OPTIONAL_COLUMN_IDS = [
  'name',
  'temperatureCelsius',
  'humidityPercent',
  'lightLux',
  'batteryPercent',
  'location',
  'lastReported',
  'serialNumber',
] as const

export const isPinnedColumn = (id: string): boolean => (PINNED_COLUMN_IDS as readonly string[]).includes(id)

// Labels shown in the column picker (data-column headers use icons where applicable).
export const PINNED_COLUMN_LABELS: Record<string, string> = {
  shipment: 'Shipment',
  status: 'Status',
  alert: 'Alert',
}

export const OPTIONAL_COLUMN_LABELS: Record<string, string> = {
  name: 'Name',
  temperatureCelsius: 'Temp',
  humidityPercent: 'Humidity',
  lightLux: 'Light',
  batteryPercent: 'Battery',
  location: 'Location',
  lastReported: 'Date Last Reported',
  serialNumber: 'Serial Number',
}

// Columns that changed pinned/optional roles in the shipment-centric layout;
// surfaced for users whose saved profile predates them (an absent id normally
// means "hidden by the user", so only these are re-added).
const MIGRATED_COLUMN_IDS = ['name'] as const

/**
 * Normalize a persisted optional-column list against the current column set:
 * saved profiles may predate the shipment-centric layout (where `shipment` was
 * optional and `name` pinned). Drops unknown/pinned ids and prepends the
 * newly-optional `name` column so it doesn't silently disappear.
 */
export const normalizeColumns = (cols: string[]): string[] => {
  const valid = cols.filter((id) => (DEFAULT_OPTIONAL_COLUMN_IDS as readonly string[]).includes(id))
  const migrated = MIGRATED_COLUMN_IDS.filter((id) => !valid.includes(id))
  return [...migrated, ...valid]
}
